import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import {
  Inbox,
  Refresh,
  TrendingUp,
  Assessment,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  border: '1px solid #e2e8f0',
  borderRadius: '16px',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const EmptyState = ({ 
  type = 'general', 
  title, 
  description, 
  actionText, 
  onAction, 
  icon: CustomIcon,
  showAction = true 
}) => {
  const getDefaultContent = () => {
    switch (type) {
      case 'no-data':
        return {
          icon: <Inbox sx={{ fontSize: 64, color: '#cbd5e1' }} />,
          title: 'No Data Available',
          description: 'There is no data to display for the selected filters. Try adjusting your date range or project selection.',
          actionText: 'Refresh Data',
        };
      case 'no-issues':
        return {
          icon: <TrendingUp sx={{ fontSize: 64, color: '#10b981' }} />,
          title: 'No Issues Detected',
          description: 'Great news! No issues were found in the selected time period. Your code quality is looking good.',
          actionText: 'View All Data',
        };
      case 'no-projects':
        return {
          icon: <Assessment sx={{ fontSize: 64, color: '#cbd5e1' }} />,
          title: 'No Projects Found',
          description: 'You don\'t have any projects configured yet. Set up your first project to start analyzing your code.',
          actionText: 'Create Project',
        };
      case 'error':
        return {
          icon: <Refresh sx={{ fontSize: 64, color: '#ef4444' }} />,
          title: 'Unable to Load Data',
          description: 'Something went wrong while loading the dashboard data. Please try again.',
          actionText: 'Retry',
        };
      default:
        return {
          icon: <Inbox sx={{ fontSize: 64, color: '#cbd5e1' }} />,
          title: 'No Content',
          description: 'There is no content to display at this time.',
          actionText: 'Refresh',
        };
    }
  };

  const content = getDefaultContent();
  const displayIcon = CustomIcon || content.icon;
  const displayTitle = title || content.title;
  const displayDescription = description || content.description;
  const displayActionText = actionText || content.actionText;

  return (
    <StyledCard>
      <CardContent sx={{ 
        p: 4, 
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 300
      }}>
        <Box sx={{ mb: 3 }}>
          {displayIcon}
        </Box>
        
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: '#1e293b',
            mb: 2,
            fontSize: { xs: '1.1rem', sm: '1.25rem' }
          }}
        >
          {displayTitle}
        </Typography>
        
        <Typography
          variant="body2"
          sx={{
            color: '#737373',
            mb: 3,
            maxWidth: 400,
            lineHeight: 1.6,
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          {displayDescription}
        </Typography>
        
        {showAction && onAction && (
          <Button
            variant="contained"
            onClick={onAction}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              backgroundColor: '#1f1f1f',
              '&:hover': {
                backgroundColor: '#1f1f1f',
              }
            }}
          >
            {displayActionText}
          </Button>
        )}
      </CardContent>
    </StyledCard>
  );
};

// Specific empty state components for different sections
export const NoDataEmptyState = (props) => (
  <EmptyState type="no-data" {...props} />
);

export const NoIssuesEmptyState = (props) => (
  <EmptyState type="no-issues" {...props} />
);

export const NoProjectsEmptyState = (props) => (
  <EmptyState type="no-projects" {...props} />
);

export const ErrorEmptyState = (props) => (
  <EmptyState type="error" {...props} />
);

export default EmptyState;
